import { LanguageSharp } from "@mui/icons-material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import { Box, Chip, Divider, Grid, Link, Skeleton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router";
import ReactPlayerWrapper from "../../Components/MediaPlayer/ReactPlayerWrapper/player-wrapper";
import MediaPlayerConsole, {
  MediaPlayerConsoleStyled,
} from "../../Components/MediaPlayer/MediaPlayerConsole/media-player-console";
import MediaPlayerConsoleSkeleton from "../../Components/MediaPlayer/MediaPlayerConsole/MediaPlayerConsoleSkeleton";
import AssetVideoResolutionChip from "../../Components/Misc/Chips/AssetChip/asset-video-resolution-chip";
import MetadataBoxAccordion from "../../Components/Misc/Metadata/MetadataBoxAccordion";
import MetadataListChipListItem from "../../Components/Misc/Metadata/MetadataListChipListItem";
import MetadataListItem from "../../Components/Misc/Metadata/MetadataListItem";
import MetadataListTextItem from "../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import CastingPanel from "../../Components/Panels/CastingPanel/casting-panel";
import SynopsisPanel from "../../Components/Panels/SynopsisPanel/syno-panel";
import MovieSlider from "../../Components/Sliders/MovieSlider";
import ContentsTable from "../../Components/Table/ContentsTable/contents-table";
import { assetService, cacheService } from "../../Core/Api/services";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos, TitleContentData } from "../../Core/Api/Types/db-asset-object-infos";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { ObjectTag } from "../../Core/Api/Types/object-tag";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { PlatformLanguageCode } from "../../Core/Api/Types/platform-language-code";
import { SetConfidentialityInfosRequest } from "../../Core/Api/Types/set-confidentiality-infos-request";
import { SetTagsPropertyRequest } from "../../Core/Api/Types/set-tags-property-request";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { AssetRightActive, GetObject, IsObjectLoading } from "../../Redux/Reducers/redux-slice-asset";
import { SetPlayerInput } from "../../Redux/Reducers/redux-slice-react-player";
import { useAppSelector } from "../../Redux/redux-store";
import { routePaths } from "../../Router/router";
import { Distinct, MoveInArray, UserHasAcl } from "../../Utils/helpers";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { NewMetadataIcon } from "../admin/Inventory/InventoryAssetSingle/AssetSingleMetadata/asset-single-metadata";
import { PreviewContext } from "../admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";

export interface TitleContentProps {
  titleContentData?: TitleContentData;
  titleActiveRightData?: AssetRightActive;
  selectedContents: DbAssetObjectInfos[];
  setSelectedContents: Dispatch<SetStateAction<DbAssetObjectInfos[]>>;
}

interface TooltipAudioVersions {
  mixes: string[];
  versions: string[];
}

enum TooltipType {
  AudioVersion,
  AudioLanguage,
  Subtitle,
}

const TitleContent: FC<TitleContentProps> = (props) => {
  const { t } = useTranslation();
  const { titleContentData, titleActiveRightData, selectedContents, setSelectedContents } = props;
  const [isSynopsisOpen, setIsSynopsisOpen] = useState(false);
  const [isCastingOpen, setIsCastingOpen] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);
  const [isWatermarkEnable, setIsWatermarkEnable] = useState(false);
  const obj = useAppSelector(GetObject);
  const isLoading = useAppSelector(IsObjectLoading);
  const dispatch = useDispatch();

  const enqueueCustomSnackbar = useCustomSnackbar();
  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();

  const maxSynoNumber = 3;

  const orderedSynopsis = useMemo(() => {
    if (!titleContentData || !titleContentData.metadataList.synopses || !titleContentData.metadataList.synopses.value)
      return [];

    let result: any[] = Object.assign([], titleContentData.metadataList.synopses.value);
    let index = 0;
    let titleLang = obj ? GetMetadataByTagName(obj, "titleOriginalLanguage")?.value?.value : null;
    let languages = ["ENG", "GBR", "FRA", "DEU"];

    if (titleLang && titleLang !== null) {
      languages.unshift(titleLang);
      languages = languages.filter((x, i) => languages.findIndex((a) => a === x) === i);
    }

    for (let i = 0; i < languages.length; ++i) {
      let matchIndex = result.findIndex((x) => x.languageCode?.value === languages[i]);

      if (matchIndex !== -1) {
        MoveInArray(result, matchIndex, index);
        index++;
      }
    }

    return result;
  }, [obj, titleContentData]);

  const _renderLoadingRowContent = useCallback(
    (height: number) => (
      <Stack sx={{ height }} spacing={3}>
        <Skeleton variant="rounded" width="80%" />
        <Skeleton variant="rounded" width="70%" />
      </Stack>
    ),
    []
  );

  const onClickSynopsisPanel = useCallback(() => {
    setIsSynopsisOpen((prevState) => !prevState);
  }, []);
  const onClickCastingPanel = useCallback(() => {
    setIsCastingOpen((prevState) => !prevState);
  }, []);

  const audioTooltip = (value: string, type: TooltipType): TooltipAudioVersions => {
    let ret: TooltipAudioVersions = {
      mixes: [],
      versions: [],
    };

    if (!obj || !obj.childObjects || !metadata) return ret;

    let audioMixMetadataId = metadata.find((x) => x.tagName === "audioMix")?.id;
    let audioAccessMetadataId = metadata.find((x) => x.tagName === "audioMixOverallAccess")?.id;
    let audioLanguageMetadataId = metadata.find((x) => x.tagName === "languageAudio")?.id;
    let audioOverallVersionMetadataId = metadata.find((x) => x.tagName === "audioOverallVersion")?.id;

    for (let i = 0; i < obj.childObjects.length; ++i) {
      let assets: DbAssetObjectInfos[] = [];

      switch (type) {
        case TooltipType.AudioLanguage:
          assets = obj.childObjects[i].childObjects!.filter(
            (x) =>
              x.metadata !== null &&
              x.metadata.findIndex((y) => y.id === audioLanguageMetadataId && y.value.value === value) > -1 &&
              x.classification === "Mezz"
          );
          break;
        case TooltipType.AudioVersion:
          assets = obj.childObjects[i].childObjects!.filter(
            (x) =>
              x.metadata !== null &&
              x.metadata.findIndex((y) => y.id === audioOverallVersionMetadataId && y.value === value) > -1 &&
              x.classification === "Mezz"
          );
          break;
      }

      for (let j = 0; j < assets.length; ++j) {
        if (!assets[j].metadata) continue;

        let audioMixMeta = assets[j].metadata!.find((x) => x.id === audioMixMetadataId);
        if (audioMixMeta !== undefined && !ret.mixes.includes(audioMixMeta.value)) ret.mixes.push(audioMixMeta.value);

        let audioAccessMeta = assets[j].metadata!.find((x) => x.id === audioAccessMetadataId);
        if (audioAccessMeta !== undefined && !ret.versions.includes(audioAccessMeta.value))
          ret.versions.push(audioAccessMeta.value);
      }
    }

    return ret;
  };

  const subtitleTooltip = (value: string): string[] => {
    let types: any[] = [];

    if (!obj || !obj.childObjects || !metadata) return [];

    let typeMetadataId = metadata.find((x) => x.tagName === "subtitleType")?.id;
    let sdhMetadataId = metadata.find((x) => x.tagName === "subtitleSME")?.id;
    let languageMetadataId = metadata.find((x) => x.tagName === "languageSubtitle")?.id;

    for (let i = 0; i < obj.childObjects.length; ++i) {
      let assets = obj.childObjects[i].childObjects!.filter(
        (x) =>
          x.metadata !== null &&
          x.metadata.findIndex((y) => y.id === languageMetadataId && y.value.value === value) > -1 &&
          x.classification === "Mezz"
      );

      for (let j = 0; j < assets.length; ++j) {
        if (!assets[j].metadata) continue;

        let typeMeta = assets[j].metadata!.find((x) => x.id === typeMetadataId);
        let sdhMeta = assets[j].metadata!.find((x) => x.id === sdhMetadataId);

        if (typeMeta !== undefined) {
          let type = {
            sdh: sdhMeta === undefined || sdhMeta === null || sdhMeta.value === false ? false : true,
            type: typeMeta.value,
          };

          if (!types.find((x) => x.type === type.type && x.sdh === type.sdh)) types.push(type);
        }
      }
    }

    return types.map((t) => t.type + (t.sdh ? " (SDH)" : ""));
  };

  const _renderTooltip = (value: string, type: TooltipType) => {
    switch (type) {
      case TooltipType.AudioVersion:
      case TooltipType.AudioLanguage:
        let res1 = audioTooltip(value, type);
        return (
          <Stack p={0.5}>
            <Typography>
              <Stack direction="row" spacing={1}>
                <GraphicEqIcon sx={{ width: 15, height: 15 }} />
                <Typography>{t("common:mixes")}</Typography>
              </Stack>
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
              {res1.mixes.map((mix, index) => (
                <Chip label={mix} key={index} size="small" />
              ))}
            </Stack>
            <Typography mt={2}>
              <Stack direction="row" spacing={1}>
                <HeadphonesIcon sx={{ width: 15, height: 15 }} />
                <Typography>{t("common:accesses")}</Typography>
              </Stack>
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
              {res1.versions.map((v, index) => (
                <Chip label={v} key={index} size="small" />
              ))}
              {res1.versions.length === 0 && (
                <Typography fontStyle="italic" variant="body2" color="text.tier">
                  {t("common:notAvailable")}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      case TooltipType.Subtitle:
        let res2 = subtitleTooltip(value);
        return (
          <Stack p={0.5}>
            <Typography>
              <Stack direction="row" spacing={1}>
                <ClosedCaptionIcon sx={{ width: 15, height: 15 }} />
                <Typography>{t("common:types")}</Typography>
              </Stack>
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
              {res2.map((v, index) => (
                <Chip label={v} key={index} size="small" />
              ))}
            </Stack>
          </Stack>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setIsConfidential(titleContentData !== undefined && titleContentData.hasConfidentialityInfos);
    setIsWatermarkEnable(titleContentData !== undefined && titleContentData.tags.includes(ObjectTag.Watermark));
  }, [titleContentData]);

  useEffect(() => {
    if (!obj) return;
    // Set player content
    dispatch(
      SetPlayerInput({
        objectId: obj.id,
        episode: GetMetadataByTagName(obj, "titleType")?.value === "Season" ? 1 : null, // assume first episode will be 1.
        shareMediaKey: null,
      })
    );
  }, [obj]);

  return (
    <Box py={2}>
      {!titleContentData || isLoading ? (
        <MediaPlayerConsoleSkeleton />
      ) : (
        <MediaPlayerConsoleStyled.StyledStack flexDirection="row">
          <MediaPlayerConsoleStyled.MediaPlayer>
            <ReactPlayerWrapper assetType={ObjectType.Video} context={PreviewContext.Title}></ReactPlayerWrapper>
          </MediaPlayerConsoleStyled.MediaPlayer>
          <MediaPlayerConsole mediaList={titleContentData.content} allLang={titleContentData.allLang} />
        </MediaPlayerConsoleStyled.StyledStack>
      )}

      <Grid container columnSpacing={2} mt={3}>
        <Grid item md={6}>
          <MetadataBoxAccordion title={t("page:metadataBoxAccordion.title.globalInfo")} icon={<ArticleOutlinedIcon />}>
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(224)
            ) : (
              <Stack gap={1}>
                <MetadataListTextItem spaced title={t("menu:title.originalTitle")}>
                  {titleContentData.name}
                </MetadataListTextItem>
                <MetadataListTextItem spaced title={t("menu:title.catalogType")}>
                  {titleContentData.metadataList.titleType?.value || "-"}
                  {titleContentData.metadataList.titleType?.value === "Season"
                    ? ` (${titleContentData.metadataList.serieSeasonEpisodeTotalNumber?.value} episodes)`
                    : ""}
                </MetadataListTextItem>
                <MetadataListTextItem spaced title={t("menu:title.videoQuality")}>
                  {titleContentData.metadataList.allVideoQualities?.value.map((chip: any) => (
                    <AssetVideoResolutionChip key={chip} label={chip} resolution={chip} size="small" sx={{ mr: 1 }} />
                  )) || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem spaced title={t("menu:title.mainDuration")}>
                  {titleContentData.metadataList.duration?.value
                    ? titleContentData.metadataList.duration?.value + " min"
                    : "-"}
                </MetadataListTextItem>
                <MetadataListTextItem spaced title={t("menu:title.programRestored")}>
                  <Chip
                    label={titleContentData.metadataList.programRestored?.value ? t("common:yes") : t("common:no")}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </MetadataListTextItem>
                <MetadataListTextItem spaced title="IMDb Id" color="text.tier">
                  {titleContentData.metadataList.imdbId?.value || "-"}
                </MetadataListTextItem>
                {titleActiveRightData && titleActiveRightData.show && (
                  <MetadataListItem spaced title={t("common:rights")} color="text.tier">
                    <Chip
                      label={titleActiveRightData.active ? t("chip:label.activeTitle") : t("chip:label.notActiveTitle")}
                      size="small"
                      variant="outlined"
                      color={titleActiveRightData.active ? "success" : "info"}
                      icon={titleActiveRightData.active ? <CheckIcon /> : <CloseIcon />}
                    />
                  </MetadataListItem>
                )}

                <Divider sx={{ mt: 1, mb: 1 }} />

                <MetadataListItem spaced title={t("common:confidentiality")}>
                  {UserHasAcl(AclTypes.aclTitleConfidentialitySet) ? (
                    <Switch
                      checked={isConfidential}
                      value={isConfidential}
                      sx={{ ml: -0.5, mt: -0.5 }}
                      size="small"
                      onChange={() => {
                        let request: SetConfidentialityInfosRequest = {
                          objectId: titleContentData.id,
                          isActivated: !isConfidential,
                          message: "Switched from UI",
                        };

                        assetService.SetConfidentialityInfos(request).then((response) => {
                          enqueueCustomSnackbar(
                            t("alert:success.titleConfidentialSetTo", { context: isConfidential ? "off" : "on" }),
                            "success"
                          );
                          setIsConfidential(!isConfidential);
                        });
                      }}
                    />
                  ) : (
                    <Chip
                      label={isConfidential ? t("common:activated") : t("chip:label.notActivated")}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </MetadataListItem>
                <MetadataListItem spaced title={t("menu:title.enableWatermark")}>
                  {UserHasAcl(AclTypes.aclTitleConfidentialitySet) && (
                    <Switch
                      checked={isWatermarkEnable}
                      value={isWatermarkEnable}
                      sx={{ ml: -0.5, mt: -0.5 }}
                      size="small"
                      onChange={() => {
                        let request: SetTagsPropertyRequest = {
                          objectId: titleContentData.id,
                          remove: isWatermarkEnable,
                          tag: ObjectTag.Watermark,
                        };

                        assetService.SetTagsProperty(request).then((response) => {
                          if (response.success) {
                            enqueueCustomSnackbar(
                              t("alert:success.titleWatermarkSetTo", { context: isWatermarkEnable ? "off" : "on" }),
                              "success"
                            );
                            setIsWatermarkEnable(!isWatermarkEnable);
                          }
                        });
                      }}
                    />
                  )}
                </MetadataListItem>
              </Stack>
            )}
          </MetadataBoxAccordion>
        </Grid>
        <Grid item md={6}>
          <MetadataBoxAccordion title={t("page:metadataBoxAccordion.title.audioAndSubtitles")} icon={<LanguageSharp />}>
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(224)
            ) : (
              <Stack gap={1}>
                <MetadataListChipListItem spaced title={t("menu:title.audioVersions")}>
                  {titleContentData.metadataList.allAudioVersions?.value.map((chip: string, index) => (
                    <Tooltip arrow title={_renderTooltip(chip, TooltipType.AudioVersion)} key={index}>
                      <Chip key={chip} label={chip} size="small" />
                    </Tooltip>
                  ))}
                </MetadataListChipListItem>
                <MetadataListChipListItem spaced title={t("common:audioLanguages")}>
                  {titleContentData.metadataList.allAudioLanguages?.value !== null
                    ? titleContentData.metadataList.allAudioLanguages?.value.map(
                        (chip: PlatformLanguageCode, index) => (
                          <Tooltip arrow title={_renderTooltip(chip.value!, TooltipType.AudioLanguage)} key={index}>
                            <Chip key={chip.id} label={chip.label} size="small" />
                          </Tooltip>
                        )
                      )
                    : "-"}
                </MetadataListChipListItem>

                <MetadataListChipListItem spaced title={t("menu:title.subtitleLanguages")}>
                  {titleContentData.metadataList.allSubtitleLanguages?.value.map(
                    (chip: PlatformLanguageCode, index) => (
                      <Tooltip
                        arrow
                        title={_renderTooltip(chip.value!, TooltipType.Subtitle)}
                        sx={{ maxWidth: 40 }}
                        key={index}
                      >
                        <Chip key={chip.id} label={chip.label} size="small" />
                      </Tooltip>
                    )
                  )}
                </MetadataListChipListItem>
              </Stack>
            )}
          </MetadataBoxAccordion>
        </Grid>
        <Grid item md={6}>
          {titleContentData && !titleContentData.metadataSections && (
            <MetadataBoxAccordion title={t("common:summary")} icon={<ArticleOutlinedIcon />}>
              {!titleContentData || isLoading ? (
                _renderLoadingRowContent(253)
              ) : (
                <Stack spacing={1}>
                  <MetadataListTextItem spaced title={t("menu:title.titleOriginalPictureFormat")}>
                    {titleContentData.metadataList.titleOriginalPictureFormat?.value || "-"}
                  </MetadataListTextItem>
                  <MetadataListTextItem spaced title={t("menu:title.productionDate")}>
                    {titleContentData.metadataList.productionDate?.value
                      ? moment(titleContentData.metadataList.productionDate?.value).format("YYYY")
                      : "-"}
                  </MetadataListTextItem>
                  <MetadataListTextItem spaced title={t("menu:title.originalLanguage")}>
                    {(titleContentData.metadataList.titleOriginalLanguage?.value.label || "-") +
                      " (" +
                      (titleContentData.metadataList.titleOriginalLanguage?.value.value || "-") +
                      ")"}
                  </MetadataListTextItem>
                  <MetadataListTextItem
                    spaced
                    title={t("menu:title.titleSecondaryOriginalAudioLanguage")}
                    color="text.tier"
                  >
                    {titleContentData.metadataList.titleSecondaryOriginalLanguages?.value.map(
                      (lang: PlatformLanguageCode) => lang.label + "(" + lang.value + ")"
                    ) || "-"}
                  </MetadataListTextItem>
                  <MetadataListTextItem spaced title={t("menu:title.titleNationalities")} color="text.tier">
                    {titleContentData.metadataList.nationalities?.value || "-"}
                  </MetadataListTextItem>
                  <MetadataListTextItem spaced title={t("menu:title.genreKeywords")}>
                    {titleContentData.metadataList.titleGenres?.value.map((chip: any) => (
                      <Chip key={chip} label={chip} size="small" sx={{ ml: 1 }} />
                    )) || "-"}
                  </MetadataListTextItem>
                  <MetadataListTextItem spaced title={t("common:note")}>
                    {titleContentData.metadataList.note?.value || "-"}
                  </MetadataListTextItem>
                </Stack>
              )}
            </MetadataBoxAccordion>
          )}
          <MetadataBoxAccordion title={t("common:castAndCrew")} icon={<PeopleAltOutlinedIcon />}>
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(221)
            ) : (
              <Stack spacing={1}>
                {Object.keys(titleContentData.casting).length !== 0 ? (
                  <>
                    {Object.keys(titleContentData.casting)[0] && (
                      <MetadataListChipListItem spaced title={Object.keys(titleContentData.casting)[0]}>
                        {Object.values(titleContentData.casting)[0]
                          .filter(Distinct)
                          .map((chip: string) => (
                            <Chip key={chip} label={chip} size="small" />
                          ))}
                      </MetadataListChipListItem>
                    )}
                    {Object.keys(titleContentData.casting)[1] && (
                      <MetadataListChipListItem spaced title={Object.keys(titleContentData.casting)[1]}>
                        {Object.values(titleContentData.casting)[1]
                          .filter(Distinct)
                          .map((chip: string) => (
                            <Chip key={chip} label={chip} size="small" />
                          ))}
                      </MetadataListChipListItem>
                    )}
                    {Object.keys(titleContentData.casting)[2] && (
                      <MetadataListChipListItem spaced title={Object.keys(titleContentData.casting)[2]}>
                        {Object.values(titleContentData.casting)[2]
                          .filter(Distinct)
                          .map((chip: string) => (
                            <Chip key={chip} label={chip} size="small" />
                          ))}
                      </MetadataListChipListItem>
                    )}
                    {Object.keys(titleContentData.casting)[3] && (
                      <MetadataListChipListItem spaced title={Object.keys(titleContentData.casting)[3]}>
                        {Object.values(titleContentData.casting)[3]
                          .filter(Distinct)
                          .map((chip: string) => (
                            <Chip key={chip} label={chip} size="small" />
                          ))}
                      </MetadataListChipListItem>
                    )}
                    <Link
                      component="button"
                      underline="hover"
                      variant="button"
                      color="primary.text"
                      onClick={onClickCastingPanel}
                      sx={{ mb: 2 }}
                    >
                      {t("button:viewMore")}
                      <ArrowForwardOutlinedIcon sx={{ ml: 1 }} />
                    </Link>
                  </>
                ) : (
                  t("page:body.noData")
                )}
              </Stack>
            )}
          </MetadataBoxAccordion>
          {!isLoading &&
            titleContentData &&
            titleContentData.metadataSections &&
            titleContentData.metadataSections.sections
              .filter((section, sectionIndex) => sectionIndex % 2 === 0)
              .map((section, sectionIndex) => (
                <MetadataBoxAccordion
                  key={sectionIndex}
                  title={section.name}
                  icon={<NewMetadataIcon icon={section.icon} />}
                >
                  <Stack spacing={1}>
                    {section.rows.map((row, rowIndex) => (
                      <MetadataListTextItem spaced key={rowIndex} title={row.label} color="text.primary">
                        {assetService.FormatMetadataRow(row)}
                      </MetadataListTextItem>
                    ))}
                  </Stack>
                </MetadataBoxAccordion>
              ))}
        </Grid>
        <Grid item md={6}>
          <MetadataBoxAccordion title={t("common:synopsis")} icon={<TranslateIcon />}>
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(245)
            ) : (
              <>
                {titleContentData.metadataList.synopses ? (
                  <>
                    {orderedSynopsis.slice(0, maxSynoNumber).map((syno, index) => (
                      <Stack key={index}>
                        <Typography
                          variant="h6"
                          component="h4"
                          fontWeight={600}
                          display="inline-flex"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="h5" component="span" mr={0.5}>
                            {syno?.languageCode?.label || syno?.languageLocalized?.isoCodeThreeLetters || "Worldwide"}
                          </Typography>
                          {t("page:body.longVersion")}
                        </Typography>
                        <Typography variant="body2" fontWeight={400} color="text.secondary" mb={2}>
                          {syno.text}
                        </Typography>
                      </Stack>
                    ))}

                    <Link
                      component="button"
                      underline="hover"
                      variant="button"
                      color="primary.text"
                      sx={{ mb: 0 }}
                      onClick={onClickSynopsisPanel}
                    >
                      {t("button:viewMore")}
                      <ArrowForwardOutlinedIcon sx={{ ml: 1 }} />
                    </Link>
                  </>
                ) : (
                  t("page:body.noData")
                )}
              </>
            )}
          </MetadataBoxAccordion>
          <MetadataBoxAccordion title={t("menu:title.localizedAndVersionedTitles")} icon={<TranslateIcon />}>
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(145)
            ) : (
              <Stack spacing={1}>
                {titleContentData.metadataList.titles
                  ? titleContentData.metadataList.titles.value.map((data: any, index) => (
                      <MetadataListTextItem
                        spaced
                        key={index}
                        title={
                          data.languageCode?.value
                            ? t("menu:title.languageVersion", {
                                label: data.languageCode.label,
                                value: data.languageCode.value,
                              })
                            : t("menu:title.worldwideVersion")
                        }
                        color="text.tier"
                      >
                        {data.text}
                      </MetadataListTextItem>
                    ))
                  : t("page:body.noData")}
              </Stack>
            )}
          </MetadataBoxAccordion>
          {!isLoading &&
            titleContentData &&
            titleContentData.metadataSections &&
            titleContentData.metadataSections.sections
              .filter((section, sectionIndex) => sectionIndex % 2 !== 0)
              .map((section, sectionIndex) => (
                <MetadataBoxAccordion
                  key={sectionIndex}
                  title={section.name}
                  icon={<NewMetadataIcon icon={section.icon} />}
                >
                  <Stack spacing={1}>
                    {section.rows.map((row, rowIndex) => (
                      <MetadataListTextItem spaced key={rowIndex} title={row.label} color="text.primary">
                        {assetService.FormatMetadataRow(row)}
                      </MetadataListTextItem>
                    ))}
                  </Stack>
                </MetadataBoxAccordion>
              ))}
        </Grid>
        <Grid item md={12}>
          <MetadataBoxAccordion
            title={t("page:metadataBoxAccordion.title.contentDetails")}
            icon={<FormatListBulletedIcon />}
            mb={0}
          >
            {!titleContentData || isLoading ? (
              _renderLoadingRowContent(200)
            ) : (
              <ContentsTable
                rows={titleContentData.content
                  .map((x) => x)
                  .sort(function (a, b) {
                    return a.title.localeCompare(b.title);
                  })}
                /*action={{
                label: "Add to order",
                onClick: () => {
                  const request: AddItemToBasketRequest = new AddItemToBasketRequest({ objectId: titleContentData.id });
                  orderService.AddItemToBasket(request);
                },
              }}*/
                selectedContents={selectedContents}
                setSelectedContents={setSelectedContents}
              />
            )}
          </MetadataBoxAccordion>
        </Grid>
      </Grid>

      {titleContentData && titleContentData.relatedTitles && (
        <Box component="section" mt={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography component="h2" variant="h3">
              {t("page:body.youMightAlsoLike")}
            </Typography>
            <Link component={RouterLink} to={routePaths.catalog.path} variant="body1" color="white" underline="hover">
              {t("common:seeAll")}
              <KeyboardArrowRightIcon fontSize="small" />
            </Link>
          </Stack>
          <MovieSlider list={titleContentData.relatedTitles} loading={false} />
        </Box>
      )}
      {titleContentData && titleContentData.casting && (
        <CastingPanel cast={titleContentData.casting} open={isCastingOpen} onClose={() => setIsCastingOpen(false)} />
      )}
      {titleContentData && titleContentData.metadataList.synopses?.value && (
        <SynopsisPanel
          list={titleContentData.metadataList.synopses?.value}
          open={isSynopsisOpen}
          onClose={() => setIsSynopsisOpen(false)}
        />
      )}
    </Box>
  );
};

export default TitleContent;
